import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import type { AppointmentsDetailsResponse } from '@/types/scheduling-api';
import { APPOINTMENT_LIST_QUERIES } from './appointment-list-keys';

/**
 * PUT /api/Appointment/{id}/Note — adds or replaces the internal note
 * (`appointment.comment` in the response). Mirrors Flutter
 * `scheduling_data_source_impl.dart:91`.
 *
 * Optimistic update: writes the new comment into every cached
 * AppointmentsDetailsResponse so the row's note indicator (and any
 * inline-rendered note text) reflects immediately.
 */
export function useAddAppointmentNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      put<void>(`/api/Appointment/${id}/Note`, { note }),
    onMutate: ({ id, note }) => {
      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      queryClient
        .getQueryCache()
        .findAll(APPOINTMENT_LIST_QUERIES)
        .forEach((q) => {
          snapshots.push({ key: q.queryKey, data: q.state.data });
        });

      queryClient.setQueriesData<AppointmentsDetailsResponse | undefined>(
        APPOINTMENT_LIST_QUERIES,
        (prev) => {
          if (!prev?.appointments) return prev;
          const idx = prev.appointments.findIndex((a) => a.id === id);
          if (idx < 0) return prev;
          const next = prev.appointments.slice();
          next[idx] = { ...next[idx], comment: note };
          return { ...prev, appointments: next };
        },
      );

      return { snapshots };
    },
    onError: (_e, _v, ctx) => {
      ctx?.snapshots.forEach(({ key, data }) =>
        queryClient.setQueryData(key, data),
      );
    },
  });
}
